/**
 * One GAM advertiser in the "By advertiser" picker: a collapsible card whose
 * header carries the advertiser name, lifecycle counts and the hub-level
 * action (create the advertiser page, or open / copy / sync / manage the
 * existing one); the body lists the advertiser's orders with the usual
 * per-campaign actions.
 */

import React from 'react';
import { Button } from '../../ui';
import type { ReportableOrder } from '../../../services/campaignReportsApi';
import type { AdvertiserCandidateGroup, AdvertiserReportPage } from '../../../services/advertiserReportsApi';
import { advertiserPageUrl, formatLifecycleCounts, isSyncLive, plural } from './shared';
import { SyncPill } from './pills';
import OrderPickerTable from './OrderPickerTable';

interface AdvertiserGroupCardProps {
  group: AdvertiserCandidateGroup;
  /** Orders to list — already filtered by the search query. */
  orders: ReportableOrder[];
  expanded: boolean;
  onToggle: () => void;
  /** The living hub covering this advertiser, once the hub list has loaded it. */
  hub: AdvertiserReportPage | null;
  copied: boolean;
  onCreateAdvertiserPage: () => void;
  onCopyHub: () => void;
  onSyncHub: () => void;
  onManageHub: () => void;
  onCreateCampaign: (order: ReportableOrder) => void;
}

const cardStyle: React.CSSProperties = {
  background: 'var(--bg-surface-1)',
  border: '1px solid var(--border-1)',
  borderRadius: 'var(--radius-lg)',
  overflow: 'hidden',
};

const headStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
  padding: '10px 14px',
  background: 'var(--cg-neutral-50)',
  cursor: 'pointer',
  userSelect: 'none',
};

const AdvertiserGroupCard: React.FC<AdvertiserGroupCardProps> = ({
  group,
  orders,
  expanded,
  onToggle,
  hub,
  copied,
  onCreateAdvertiserPage,
  onCopyHub,
  onSyncHub,
  onManageHub,
  onCreateCampaign,
}) => {
  const hubSlug = group.existingAdvertiserPageSlug;
  const hubUrl = hub?.url ?? (hubSlug ? advertiserPageUrl(hubSlug) : null);
  const syncing = hub ? isSyncLive(hub) : false;
  const filtered = orders.length < group.orders.length;

  return (
    <div style={cardStyle}>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        style={{ ...headStyle, borderBottom: expanded ? '1px solid var(--border-1)' : 'none' }}
      >
        <span style={{ fontSize: 12, color: 'var(--fg-4)', width: 12, display: 'inline-block' }}>{expanded ? '▾' : '▸'}</span>
        <span style={{ fontSize: 14.5, fontWeight: 800, color: 'var(--fg-1)' }}>{group.advertiserName}</span>
        <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>{formatLifecycleCounts(group.counts)}</span>
        {hub && <SyncPill page={hub} />}

        {/* Hub-level actions — clicks must not toggle the card. */}
        <span
          style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {hubSlug ? (
            <>
              <a href={hubUrl ?? '#'} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                <Button size="sm" variant="subtle">Advertiser page ↗</Button>
              </a>
              <Button
                size="sm"
                variant="ghost"
                onClick={onCopyHub}
                disabled={!hub}
                title={hub ? 'Copy the hub URL and its passcode' : 'Loading the advertiser page…'}
              >
                {copied ? 'Copied ✓' : 'Copy link + PIN'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={onSyncHub}
                disabled={syncing || !!hub?.revokedAt}
                title={hub?.revokedAt ? 'Restore the advertiser page first' : 'Discover new campaigns and generate their reports now'}
              >
                {syncing ? 'Syncing…' : 'Sync now'}
              </Button>
              <Button size="sm" variant="ghost" onClick={onManageHub}>Manage</Button>
            </>
          ) : (
            <Button
              size="sm"
              variant="primary"
              onClick={onCreateAdvertiserPage}
              disabled={!group.advertiserId}
              title={group.advertiserId ? 'One passcode-protected page listing every campaign of this advertiser' : 'GAM did not resolve an advertiser id for these orders'}
            >
              Create advertiser page
            </Button>
          )}
        </span>
      </div>

      {expanded && (
        <>
          {orders.length === 0 ? (
            <div style={{ padding: '12px 14px', fontSize: 12.5, color: 'var(--fg-4)' }}>No campaigns match the search.</div>
          ) : (
            <OrderPickerTable embedded orders={orders} onCreate={onCreateCampaign} />
          )}
          {filtered && orders.length > 0 && (
            <div style={{ padding: '8px 14px', fontSize: 11.5, color: 'var(--fg-4)' }}>
              Showing {orders.length} of {plural(group.orders.length, 'campaign')} — the search hides the rest.
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdvertiserGroupCard;
